import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { Loader2, Bot, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '@/hooks/useProfile';

interface LogCustomWorkoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  defaultActivityName?: string;
}

interface CompletedWorkout {
  activityName: string;
  duration: number;
  caloriesBurned?: number;
  difficulty: string;
  notes?: string;
}

export const LogCustomWorkoutModal = ({ isOpen, onClose, onSuccess, defaultActivityName }: LogCustomWorkoutModalProps) => {
  const { user } = useAuth();
  const { profile } = useProfile();
  const coachName = profile?.ai_coach_name || 'Arnold';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activityName, setActivityName] = useState(defaultActivityName || '');
  const [duration, setDuration] = useState('');
  const [caloriesBurned, setCaloriesBurned] = useState('');
  const [notes, setNotes] = useState('');
  const [difficulty, setDifficulty] = useState('medium');
  const [showAnalyzePopup, setShowAnalyzePopup] = useState(false);
  const [completedWorkout, setCompletedWorkout] = useState<CompletedWorkout | null>(null);

  useEffect(() => {
    if (isOpen) setActivityName(defaultActivityName || '');
  }, [isOpen, defaultActivityName]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('You must be logged in to log a workout');
      return;
    }

    if (!activityName.trim() || !duration) {
      toast.error('Please fill in activity name and duration');
      return;
    }

    setLoading(true);

    try {
      // Create the workout record
      const { data: workout, error: workoutError } = await supabase
        .from('workouts')
        .insert({
          user_id: user.id,
          title: activityName.trim(),
          workout_type: 'custom',
          duration_minutes: parseInt(duration),
          calories_burned: caloriesBurned ? parseInt(caloriesBurned) : null,
          difficulty_level: difficulty,
          completed_at: new Date().toISOString(),
          notes: notes.trim() || null,
        })
        .select()
        .single();

      if (workoutError) throw workoutError;

      toast.success('Custom workout logged successfully!');
      
      // Store completed workout data for Arnold analysis
      setCompletedWorkout({
        activityName: activityName.trim(),
        duration: parseInt(duration),
        caloriesBurned: caloriesBurned ? parseInt(caloriesBurned) : undefined,
        difficulty,
        notes: notes.trim() || undefined,
      });
      
      // Reset form
      setActivityName('');
      setDuration('');
      setCaloriesBurned('');
      setNotes('');
      setDifficulty('medium');
      
      // Show analyze popup - DON'T call onSuccess yet, wait until popup is dismissed
      setShowAnalyzePopup(true);
    } catch (error: any) {
      console.error('Error logging custom workout:', error);
      toast.error(error.message || 'Failed to log workout');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeWithArnold = () => {
    if (!completedWorkout) return;
    
    // Build workout data for Arnold - same pattern as gym workouts
    const customWorkoutData = {
      type: 'custom',
      title: completedWorkout.activityName,
      duration: completedWorkout.duration,
      difficulty: completedWorkout.difficulty,
      caloriesBurned: completedWorkout.caloriesBurned,
      notes: completedWorkout.notes,
    };
    
    const message = `Hey ${coachName}! I just completed a custom workout "${completedWorkout.activityName}". Can you analyze this activity and give me feedback on the benefits, recovery tips, and how it fits into my fitness goals?`;
    
    // Use React Router state - same reliable pattern as gym workouts
    navigate('/chat', {
      state: {
        workoutData: customWorkoutData,
        fromWorkout: true,
        message
      }
    });
  };

  const handleSkipAnalysis = () => {
    setShowAnalyzePopup(false);
    setCompletedWorkout(null);
    onSuccess?.(); // Call onSuccess now that popup is dismissed
    onClose();
  };

  // Show the analyze popup after workout completion
  if (showAnalyzePopup && completedWorkout) {
    return (
      <Dialog open={true} onOpenChange={handleSkipAnalysis}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-primary" />
              Analyze with {coachName}?
            </DialogTitle>
            <DialogDescription>
              Get personalized feedback on your {completedWorkout.activityName} workout from {coachName}, your AI fitness coach.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-2">
            <div className="bg-muted/50 rounded-lg p-3 text-sm">
              <p className="text-muted-foreground">
                {coachName} can analyze your workout and provide:
              </p>
              <ul className="mt-2 space-y-1 text-muted-foreground">
                <li>• Benefits & muscle groups worked</li>
                <li>• Recovery recommendations</li>
                <li>• Tips to improve next time</li>
              </ul>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleSkipAnalysis}
                className="flex-1"
              >
                Skip
              </Button>
              <Button
                onClick={handleAnalyzeWithArnold}
                className="flex-1 bg-gradient-primary text-black"
              >
                <Bot className="mr-2 h-4 w-4" />
                Analyze
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Log Custom Workout</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="activity">Activity Name *</Label>
            <Input
              id="activity"
              placeholder="e.g., Swimming, Mountain Climbing, Skiing"
              value={activityName}
              onChange={(e) => setActivityName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="duration">Duration (minutes) *</Label>
            <Input
              id="duration"
              type="number"
              placeholder="60"
              min="1"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="calories">Calories Burned (optional)</Label>
            <Input
              id="calories"
              type="number"
              placeholder="300"
              min="0"
              value={caloriesBurned}
              onChange={(e) => setCaloriesBurned(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="difficulty">Difficulty</Label>
            <Select value={difficulty} onValueChange={setDifficulty}>
              <SelectTrigger id="difficulty">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="easy">Easy</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Add any notes about this workout..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-primary text-black"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Logging...
                </>
              ) : (
                'Log Workout'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};